<template>
  <div v-if="hasTasks" class="import-task-float" :class="{ collapsed }">
    <!-- 折叠态：右侧小标签 -->
    <div v-if="collapsed" class="collapsed-tab" @click="collapsed = false">
      <div class="tab-title">导入</div>
      <div class="tab-count">{{ visibleTasks.length }}</div>
    </div>

    <!-- 展开态：任务面板 -->
    <div v-else class="panel">
      <div class="header">
        <div class="actions">
          <Button size="small" type="text" @click="collapsed = true">
            缩小
          </Button>
          <Button
            v-if="canClosePanel"
            size="small"
            type="text"
            @click="closePanel"
          >
            关闭
          </Button>
        </div>
      </div>
      <div class="list">
        <div v-if="!visibleTasks.length" class="empty">正在加载任务进度...</div>
        <div v-for="t in visibleTasks" :key="t.TaskId" class="item">
          <div class="top">
            <div class="name">
              <span class="status" :class="statusClass(t.Status)">
                {{ statusText(t.Status) }}
              </span>
              <span class="taskid" :title="t.ImportLabel || t.TaskId">
                {{ t.ImportLabel || `任务：${t.TaskId}` }}
              </span>
            </div>
            <div class="right">
              <span v-if="t.Status === 'Success'" class="hint">
                10秒后自动关闭
              </span>
              <Icon
                v-if="t.Status === 'Failed' || t.Status === 'PartialSuccess'"
                type="ios-close"
                class="close"
                @click="dismissTask(t.TaskId)"
              />
            </div>
          </div>
          <div class="progress-row">
            <el-progress
              :percentage="t.Progress || 0"
              :status="progressStatus(t.Status)"
              :stroke-width="10"
            />
          </div>
          <div
            v-if="
              (t.Status === 'Failed' || t.Status === 'PartialSuccess') &&
              t.ErrorMessage
            "
            class="error"
          >
            {{ t.ErrorMessage }}
          </div>
          <div
            v-if="
              (t.Status === 'Failed' || t.Status === 'PartialSuccess') &&
              canShowImportErrorHandle(t.ErrorMessage)
            "
            class="ops"
          >
            <Button size="small" type="error" @click="openErrorHandle(t)">
              点击处理错误
            </Button>
          </div>
          <div class="meta">
            <span>成功：{{ t.SuccessRows || 0 }}</span>
            <span>失败：{{ t.FailedRows || 0 }}</span>
            <span>总行：{{ t.TotalRows || 0 }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getImportTaskList } from '@/api/importBackgroundTask';
import { canShowImportErrorHandle } from '@/libs/importBackgroundTaskHelpers';

const STORAGE_KEY = 'oa_import_bg_task_ids';
const STORAGE_COLLAPSED_KEY = 'oa_import_bg_task_collapsed';
const STORAGE_LABEL_KEY = 'oa_import_bg_task_labels';

export default {
  name: 'ImportTaskFloat',
  data() {
    return {
      tasks: {},
      trackedIds: [],
      timer: null,
      successTimers: {},
      collapsed: false,
    };
  },
  computed: {
    userId() {
      return this.$store && this.$store.state && this.$store.state.user
        ? this.$store.state.user.userId
        : '';
    },
    visibleTasks() {
      return Object.values(this.tasks).sort((a, b) => {
        const ta = new Date(a.CreateTime || 0).getTime();
        const tb = new Date(b.CreateTime || 0).getTime();
        return tb - ta;
      });
    },
    hasTasks() {
      // trackedIds 改为响应式，确保新增任务时浮窗立即显示
      return this.trackedIds.length > 0;
    },
    hasRunningOrPending() {
      return this.visibleTasks.some(
        (t) => t.Status === 'Running' || t.Status === 'Pending',
      );
    },
    hasFailed() {
      return this.visibleTasks.some(
        (t) => t.Status === 'Failed' || t.Status === 'PartialSuccess',
      );
    },
    canClosePanel() {
      // 执行中/解析中：只能缩小不能关闭
      // 有失败或部分失败：让用户手动处理或删除（不提供一键关闭，避免遗漏）
      return !this.hasRunningOrPending && !this.hasFailed;
    },
  },
  mounted() {
    this.loadTrackedIds();
    this.collapsed = localStorage.getItem(STORAGE_COLLAPSED_KEY) === '1';
    window.addEventListener('oa-import-task:add', this.onAddTaskEvent);
    this.startPolling();
  },
  beforeDestroy() {
    window.removeEventListener('oa-import-task:add', this.onAddTaskEvent);
    this.stopPolling();
    Object.values(this.successTimers).forEach((x) => clearTimeout(x));
  },
  watch: {
    collapsed(val) {
      localStorage.setItem(STORAGE_COLLAPSED_KEY, val ? '1' : '0');
    },
  },
  methods: {
    canShowImportErrorHandle,
    normalizeTask(raw) {
      if (!raw) return null;
      const taskId = raw.TaskId || raw.taskId || raw.taskid || '';
      if (!taskId) return null;
      return {
        TaskId: taskId,
        OperationId: raw.OperationId || raw.operationId || raw.operationid,
        ImportCategoriesType:
          raw.ImportCategoriesType ||
          raw.importCategoriesType ||
          raw.importcategoriestype,
        Status: raw.Status || raw.status,
        Progress: raw.Progress || raw.progress || 0,
        TotalRows: raw.TotalRows || raw.totalRows || raw.totalrows || 0,
        SuccessRows: raw.SuccessRows || raw.successRows || raw.successrows || 0,
        FailedRows: raw.FailedRows || raw.failedRows || raw.failedrows || 0,
        ErrorMessage:
          raw.ErrorMessage || raw.errorMessage || raw.errormessage || '',
        CreateTime: raw.CreateTime || raw.createTime || raw.createtime,
        StartTime: raw.StartTime || raw.startTime || raw.starttime,
        EndTime: raw.EndTime || raw.endTime || raw.endtime,
        ImportLabel: this.getTaskLabels()[taskId] || '',
      };
    },
    getTaskLabels() {
      try {
        const raw = localStorage.getItem(STORAGE_LABEL_KEY);
        const map = raw ? JSON.parse(raw) : {};
        return map && typeof map === 'object' ? map : {};
      } catch (e) {
        return {};
      }
    },
    setTaskLabel(taskId, label) {
      if (!taskId || !label) return;
      const map = this.getTaskLabels();
      map[taskId] = label;
      localStorage.setItem(STORAGE_LABEL_KEY, JSON.stringify(map));
      if (this.tasks[taskId]) {
        this.$set(this.tasks[taskId], 'ImportLabel', label);
      }
    },
    startPolling() {
      if (this.timer) return;
      this.pollOnce();
      this.timer = setInterval(this.pollOnce, 3000);
    },
    stopPolling() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    },
    getTrackedIds() {
      return Array.isArray(this.trackedIds) ? this.trackedIds : [];
    },
    setTrackedIds(list) {
      const safeList = Array.isArray(list) ? list : [];
      this.trackedIds = safeList;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(safeList));
    },
    addTrackedId(taskId) {
      if (!taskId) return;
      const ids = this.getTrackedIds();
      if (!ids.includes(taskId)) {
        ids.unshift(taskId);
        this.setTrackedIds(ids.slice(0, 30)); // 防止无限增长
      }
    },
    loadTrackedIds() {
      let ids = [];
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const list = raw ? JSON.parse(raw) : [];
        ids = Array.isArray(list) ? list : [];
      } catch (e) {
        ids = [];
      }
      this.trackedIds = ids;
      ids.forEach((id) => {
        if (!this.tasks[id])
          this.$set(this.tasks, id, { TaskId: id, Progress: 0 });
      });
    },
    onAddTaskEvent(e) {
      const taskId = e && e.detail ? e.detail.taskId : '';
      const importLabel = e && e.detail ? e.detail.importLabel : '';
      this.addTrackedId(taskId);
      this.setTaskLabel(taskId, importLabel);
      if (!this.tasks[taskId]) {
        this.$set(this.tasks, taskId, {
          TaskId: taskId,
          Progress: 0,
          ImportLabel: importLabel || '',
        });
      }
      this.pollOnce();
    },
    async pollOnce() {
      // 未登录/未拿到 userId 就不轮询
      if (!this.userId) return;

      const trackedIds = this.getTrackedIds();
      if (!trackedIds.length) return;

      try {
        const { data } = await getImportTaskList({
          page: 1,
          pageSize: 50,
          createUserId: this.userId,
        });
        if (!data || !data.success || !Array.isArray(data.data)) return;

        const serverMap = {};
        data.data.forEach((t) => {
          const nt = this.normalizeTask(t);
          if (nt) serverMap[nt.TaskId] = nt;
        });

        trackedIds.forEach((id) => {
          const fromServer = serverMap[id];
          if (fromServer) this.$set(this.tasks, id, fromServer);
        });

        // 成功任务：10 秒后自动移除（只做一次）
        trackedIds.forEach((id) => {
          const t = this.tasks[id];
          if (t && t.Status === 'Success' && !this.successTimers[id]) {
            this.successTimers[id] = setTimeout(() => {
              this.dismissTask(id);
              this.successTimers[id] = null;
            }, 10000);
          }
        });
      } catch (e) {
        // 忽略轮询错误，避免打扰用户
      }
    },
    dismissTask(taskId) {
      const ids = this.getTrackedIds().filter((x) => x !== taskId);
      this.setTrackedIds(ids);
      this.$delete(this.tasks, taskId);
      const labelMap = this.getTaskLabels();
      if (labelMap[taskId]) {
        delete labelMap[taskId];
        localStorage.setItem(STORAGE_LABEL_KEY, JSON.stringify(labelMap));
      }
      if (this.successTimers[taskId]) {
        clearTimeout(this.successTimers[taskId]);
        this.successTimers[taskId] = null;
      }
    },
    clearSuccess() {
      const ids = this.getTrackedIds();
      ids.forEach((id) => {
        const t = this.tasks[id];
        if (t && t.Status === 'Success') this.dismissTask(id);
      });
    },
    closePanel() {
      // 只在 canClosePanel 时允许关闭：直接清空 trackedIds
      this.setTrackedIds([]);
      localStorage.removeItem(STORAGE_LABEL_KEY);
      this.tasks = {};
      Object.values(this.successTimers).forEach((x) => clearTimeout(x));
      this.successTimers = {};
    },
    statusText(status) {
      if (status === 'Pending') return '解析中';
      if (status === 'Running') return '导入中';
      if (status === 'Success') return '成功';
      if (status === 'PartialSuccess') return '部分失败';
      if (status === 'Failed') return '失败';
      return status || '未知';
    },
    statusClass(status) {
      if (status === 'Pending') return 'pending';
      if (status === 'Running') return 'running';
      if (status === 'Success') return 'success';
      if (status === 'PartialSuccess') return 'failed';
      if (status === 'Failed') return 'failed';
      return '';
    },
    progressStatus(status) {
      if (status === 'PartialSuccess') return 'exception';
      if (status === 'Failed') return 'exception';
      if (status === 'Success') return 'success';
      return null;
    },
    openErrorHandle(task) {
      window.dispatchEvent(
        new CustomEvent('oa-import-task:open-errors', { detail: task || {} }),
      );
      this.collapsed = false;
    },
  },
};
</script>

<style lang="less" scoped>
.import-task-float {
  position: fixed;
  right: 16px;
  bottom: 16px;
  width: 320px;
  max-height: 60vh;
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  overflow: hidden;
}

.import-task-float.collapsed {
  width: auto;
  background: transparent;
  border: 0;
  box-shadow: none;
  overflow: visible;
}

.collapsed-tab {
  position: fixed;
  right: 0;
  bottom: 120px;
  width: 52px;
  background: #2d8cf0;
  color: #fff;
  border-radius: 10px 0 0 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  padding: 10px 8px;
  cursor: pointer;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.tab-title {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
}

.tab-count {
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.panel {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.title {
  font-weight: 600;
  color: #17233d;
}

.list {
  padding: 8px 12px;
  overflow: auto;
  max-height: calc(60vh - 46px);
}

.empty {
  padding: 14px 10px;
  color: #808695;
  font-size: 12px;
}

.item {
  padding: 10px 10px;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  margin-bottom: 8px;
}

.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.taskid {
  color: #515a6e;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hint {
  font-size: 12px;
  color: #808695;
}

.close {
  cursor: pointer;
  color: #c5c8ce;
  font-size: 18px;
  &:hover {
    color: #ed4014;
  }
}

.status {
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 18px;
}
.status.pending {
  background: #fff7e6;
  color: #fa8c16;
}
.status.running {
  background: #e6f7ff;
  color: #1890ff;
}
.status.success {
  background: #f6ffed;
  color: #52c41a;
}
.status.failed {
  background: #fff1f0;
  color: #f5222d;
}

.progress-row {
  margin: 6px 0 4px;
}

.error {
  margin-top: 6px;
  font-size: 12px;
  color: #ed4014;
  white-space: normal;
  word-break: break-all;
}

.ops {
  margin-top: 4px;
}

.meta {
  margin-top: 6px;
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #808695;
}
</style>
